import { Link, useParams } from '@tanstack/react-router';
import { SystemStatusBadge } from '@tempo-io/sui-badge';
import { toast } from '@tempo-io/sui-sonner';
import { TableCell, TableRow } from '@tempo-io/sui-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import type { SyncType } from '@/common/api/types/workIntegrations';
import { SyncSplitButton } from '@/common/components/sync-work-integrations/SyncSplitButton';
import { useSourceSystemLinkLabel } from '@/common/hooks/useSourceSystemLinkLabel';
import type { IntegrationSyncFailedItem } from '@/features/settings/work-integrations/api/syncFailures';
import { SyncFailureType } from '@/features/settings/work-integrations/api/syncFailures';
import { useIsAdoPushGated } from '@/features/settings/work-integrations/hooks/useIsAdoPushGated';
import {
  type SyncScope,
  useScopedSync,
} from '@/features/settings/work-integrations/hooks/useScopedSync';
import { useGetAllWorkIntegrationsById } from '@/features/settings/work-integrations/queries/useGetAllWorkIntegrations';
import { useInstanceFieldMappingsQuery } from '@/features/settings/work-integrations/queries/useInstanceFieldMappingsQuery';
import { buildSourceItemLink } from '@/features/settings/work-integrations/utils/buildSourceItemLink';
import {
  COL_ACTIONS,
  COL_AFFECTED,
  COL_DIRECTION,
  COL_ITEM,
  COL_LOOP,
  COL_MESSAGE,
  COL_TYPE,
} from './syncFailureColumns';

export type SyncFailureRowVariant = 'integration' | 'item';

type Props = {
  failure: IntegrationSyncFailedItem;
  variant?: SyncFailureRowVariant;
};

const isFieldValueMappingFailure = (fields: Record<string, string>): boolean =>
  Object.keys(fields).length > 0 && Object.keys(fields).every((k) => /^\d+$/.test(k));

const SOURCE_ITEM_ID_PATTERN = /^\p{L}[\p{L}\p{N}_]*-[0-9]+$|^[0-9]+$/u;

const buildRetryScope = (failure: IntegrationSyncFailedItem): SyncScope | null => {
  const scope: SyncScope = {};

  if (failure.itemId != null) scope.itemIds = [failure.itemId];
  if (failure.sourceItemKey != null && SOURCE_ITEM_ID_PATTERN.test(failure.sourceItemKey)) {
    scope.sourceItemIds = [failure.sourceItemKey];
  }

  return scope.itemIds || scope.sourceItemIds ? scope : null;
};

const BADGE_VARIANT_BY_FAILURE_TYPE: Record<SyncFailureType, 'error' | 'warning' | 'default'> = {
  [SyncFailureType.PERMANENT]: 'error',
  [SyncFailureType.TRANSIENT]: 'warning',
  [SyncFailureType.RECOVERABLE]: 'warning',
  [SyncFailureType.UNKNOWN]: 'default',
};

export function SyncFailureRow({ failure, variant = 'integration' }: Props) {
  const { t } = useTranslation();
  const { org } = useParams({ strict: false }) as { org?: string };
  const { data: integrationsById } = useGetAllWorkIntegrationsById();
  const integration = integrationsById?.[failure.integrationId];
  const loopId = integration?.loopId;
  const instanceId = integration?.instanceId;
  const showLoopColumn = variant === 'integration';
  const showItemColumn = variant === 'integration';
  const hidePushOption = useIsAdoPushGated(integration?.sourceSystemType);

  const sourceItemLink = buildSourceItemLink(
    integration?.sourceSystemType,
    integration?.instanceUrl,
    integration?.projectId,
    failure.sourceItemKey,
  );
  const sourceItemLinkTitle = useSourceSystemLinkLabel(integration?.sourceSystemType);

  const { data: fieldMappings = [] } = useInstanceFieldMappingsQuery({ cloudId: instanceId });
  const fieldNameById = new Map(fieldMappings.map((m) => [String(m.id), m.tempoFieldName]));

  const itemLabel =
    failure.sourceItemKey ??
    (failure.itemId !== null
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.unknownItem, {
          itemId: failure.itemId,
        })
      : t(($) => $.common.settings['work-integrations'].detail.syncFailures.untitledItem));

  const directionLabel =
    failure.syncDirection === 'PULL'
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.direction.pull)
      : t(($) => $.common.settings['work-integrations'].detail.syncFailures.direction.push);

  const typeVariant = BADGE_VARIANT_BY_FAILURE_TYPE[failure.type];
  const typeLabel: string = (() => {
    switch (failure.type) {
      case SyncFailureType.PERMANENT:
        return t(($) => $.common.settings['work-integrations'].detail.syncFailures.type.permanent);
      case SyncFailureType.TRANSIENT:
        return t(($) => $.common.settings['work-integrations'].detail.syncFailures.type.transient);
      case SyncFailureType.RECOVERABLE:
        return t(
          ($) => $.common.settings['work-integrations'].detail.syncFailures.type.recoverable,
        );
      case SyncFailureType.UNKNOWN:
        return t(($) => $.common.settings['work-integrations'].detail.syncFailures.type.unknown);
    }
  })();

  const failedFields = failure.failedFields ?? {};
  const fieldValueMapping = isFieldValueMappingFailure(failedFields);

  const {
    sync,
    isScopeMissing,
    integrationIsSyncing,
    isDisabled: retryDisabled,
    inProgress: retryInProgress,
  } = useScopedSync({
    workIntegrationId: failure.integrationId,
    integrationStatus: integration?.status,
    scope: buildRetryScope(failure),
  });

  const retryUnavailableReason = isScopeMissing
    ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryUnidentified)
    : integrationIsSyncing
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryWhileSyncing)
      : undefined;

  const retry = (syncType: SyncType) => {
    sync(syncType, {
      onSuccess: (result) => {
        if (result.type === 'no-items') {
          toast.info(
            t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryNothingToSync),
          );
        }
      },
    });
  };

  return (
    <TableRow data-testid="sync-failure-row">
      {showLoopColumn && (
        <TableCell className={`${COL_LOOP} align-top font-mono text-xs`}>{loopId ?? '—'}</TableCell>
      )}
      {showItemColumn && (
        <TableCell className={`${COL_ITEM} align-top font-mono text-xs`}>
          {sourceItemLink ? (
            <a
              href={sourceItemLink}
              target="_blank"
              rel="noopener noreferrer"
              title={sourceItemLinkTitle}
              className="block truncate text-text-link hover:underline"
            >
              {itemLabel}
            </a>
          ) : (
            <span className="block truncate">{itemLabel}</span>
          )}
        </TableCell>
      )}
      <TableCell className={`${COL_DIRECTION} align-top`}>
        <SystemStatusBadge variant="default">{directionLabel}</SystemStatusBadge>
      </TableCell>
      <TableCell className={`${COL_TYPE} align-top`}>
        <SystemStatusBadge variant={typeVariant}>{typeLabel}</SystemStatusBadge>
      </TableCell>
      <TableCell
        className={`${COL_MESSAGE} align-top text-sm text-text-neutral-primary break-words`}
      >
        {failure.message}
      </TableCell>
      <TableCell className={`${COL_AFFECTED} align-top text-xs text-text-neutral-secondary`}>
        {fieldValueMapping ? (
          <ul className="space-y-1">
            {Object.keys(failedFields).map((mappingId) => {
              const name = fieldNameById.get(mappingId) ?? mappingId;
              return (
                <li key={mappingId}>
                  {variant === 'integration' && instanceId && org ? (
                    <Link
                      to="/org/$org/settings/work-integrations"
                      params={{ org }}
                      search={{ instanceId, openValueMapping: Number(mappingId) }}
                      target="_blank"
                      className="text-text-link hover:underline"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span>{name}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          Object.keys(failedFields).length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{Object.keys(failedFields).join(', ')}</span>
              </TooltipTrigger>
              <TooltipContent>
                <ul className="space-y-1">
                  {Object.keys(failedFields).map((field) => (
                    <li key={field}>
                      <span className="font-medium">{field}:</span> {failedFields[field]}
                    </li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          )
        )}
      </TableCell>
      <TableCell className={`${COL_ACTIONS} align-top`}>
        <SyncSplitButton
          labels={{
            action: t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryItem),
            options: t(
              ($) => $.common.settings['work-integrations'].detail.syncFailures.retryOptions,
            ),
            pull: t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryPull),
            push: t(($) => $.common.settings['work-integrations'].detail.syncFailures.retryPush),
          }}
          primarySyncType={failure.syncDirection}
          onSelect={retry}
          disabled={retryDisabled}
          inProgress={retryInProgress}
          unavailableReason={retryUnavailableReason}
          testIdPrefix="sync-failure-retry"
          hidePushOption={hidePushOption}
        />
      </TableCell>
    </TableRow>
  );
}
